import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EMPTY_PLAYBACK_STATE,
  PlaybackController,
  PlaybackState,
} from '../shared/contracts/playback-controller';

/**
 * One vessel this operator has located — via search, Master, Voyage or a kept
 * save. Mirrors `LocatedVesselRow` in
 * `ais/voyage-radar/toolbar/located-vessels.controller.ts`, extended with the
 * two datasets that are their own priced actions here (design decision
 * 2026-08-25 — no bundled "lookup").
 */
export interface VesselFinderVesselRow {
  mmsi: string | null;
  imo: string | null;
  /** Broadcast name, or null for an anonymous hull (the row shows the identifier). */
  name: string | null;
  /** ISO/UTC stamp of the fix the marker is drawn at. */
  timeUtc: string | null;
  /** Fix older than 48h — drawn hollow, captioned "last known", never "latest". */
  stale: boolean;
  /** Persisted in the sensor's vesselHistory — survives a reload. */
  kept: boolean;
  /** Present only once the Master action has been bought for this hull. */
  master?: Record<string, any> | null;
  /** Present only once the Voyage action has been bought for this hull. */
  voyage?: Record<string, any> | null;
  /** Present only once the Port calls action has been bought for this hull. */
  portCalls?: Array<Record<string, any>> | null;

  // ── The rest of `vesselFinderNormalize.js`'s flat AIS block — carried
  // through from search/Master/Voyage so `VfLocatedLayer` has a fix to draw
  // and `buildVesselFinderPopup` has a card to render. All optional: a bare
  // search hit may not have every field, and none of them existed on this row
  // before the fix that widened it (search used to discard all of them).
  latitude?: number | null;
  longitude?: number | null;
  speed?: number | null;
  course?: number | null;
  heading?: number | null;
  destination?: string | null;
  lastPort?: string | null;
  eta?: string | null;
  positionSource?: string | null;
  callSign?: string | null;
  shipType?: string | null;
  typeCode?: number | null;
  flag?: string | null;
  /** Same instant as `timeUtc`, under the name `buildVesselFinderPopup` reads. */
  timestamp?: string | null;
}

/**
 * `mmsi` is always a string (`vesselFinderNormalize.js`'s `str()`), but `imo`
 * is always a NUMBER (its `sentinel()`) — a real backend field-shape
 * inconsistency, not a typo, kept "the same shape `voyageRadarApi.normalizeVessel`
 * already produces" per that file's own header. Every row entering this
 * controller's state is sanitized through here so `.mmsi`/`.imo` are always
 * a real string or null, matching the `VesselFinderVesselRow` type this file
 * declares — without it, `.includes()`/other string methods elsewhere (the
 * search drawer's local filter, row keys) throw on a raw IMO number.
 */
function idStr(v: unknown): string | null {
  return v == null || v === '' ? null : String(v);
}

function sanitizeRow(row: VesselFinderVesselRow): VesselFinderVesselRow {
  return { ...row, mmsi: idStr(row.mmsi), imo: idStr(row.imo) };
}

/**
 * Does this row carry a position we can actually put on the map?
 *
 * `Number(null)` is 0 — a real coordinate, not "no fix" — so the null check
 * has to come first, or a hull with no stored position would read as being at
 * 0,0 in the Gulf of Guinea.
 *
 * Lives here rather than in `VfLocatedLayer` because two callers need the SAME
 * answer: the layer (which refuses to draw a positionless row) and the search
 * drawer's local list (whose only action is "center on this hull", which a
 * positionless row cannot satisfy). They disagreed before — a row reached
 * through the Port calls drawer's own MMSI box has no fix, so it was listed
 * but never drawn, and clicking it silently did nothing.
 */
export function vesselRowHasFix(row: VesselFinderVesselRow): boolean {
  const lat = Number(row.latitude);
  const lng = Number(row.longitude);
  return (
    row.latitude != null &&
    row.longitude != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  );
}

/** "Kept here (the newest 50)" — same cap Voyage Radar's own Watched Vessels list uses. */
const MAX_LOCATED = 50;


/**
 * The VesselFinder state — the generic contract plus the fields only this
 * controller writes.
 */
export interface VesselFinderState extends PlaybackState {
  /** Every vessel search/Master/Voyage/save has produced (empty = none). */
  locatedVessels?: VesselFinderVesselRow[];
  /**
   * The CURRENT search's raw hits — transient, cleared by the next search.
   * Not persisted: "a search that is browsed and abandoned should leave
   * nothing on the task document" (backend plan §4.1). A hit only becomes a
   * `locatedVessels` row once the user acts on it (Save, Master, Voyage, Port
   * calls).
   */
  searchResults?: VesselFinderVesselRow[];
  /**
   * The search control's satellite toggle. Read by the search action to pick
   * `searchActionKey` — never a hidden default (overview §7 Q3, answered).
   */
  useSatellite?: boolean;
}

/**
 * VesselFinder's controller — drives the search box, the satellite toggle and
 * the located-vessels list. No map facade (frontend plan Q4: "located markers
 * only — no snapshot layer, no clustering"), so unlike Voyage Radar's map
 * controller this is the ONLY controller the module needs: there is no
 * two-tier map, no playback archive, no track transport to coordinate.
 *
 * The transport contract is implemented as no-ops, exactly as
 * `VoyageVesselController` does — a list of located hulls is not a timeline,
 * and `PlaybackController` is the price of riding the shared toolbar host.
 */
@Injectable({ providedIn: 'root' })
export class VesselFinderMapController extends PlaybackController {
  private _state$ = new BehaviorSubject<VesselFinderState>({
    ...EMPTY_PLAYBACK_STATE,
    locatedVessels: [],
    searchResults: [],
    useSatellite: false,
  });
  readonly state$: Observable<VesselFinderState> = this._state$.asObservable();

  /**
   * Set once by the map facade when this operator's sensor is opened on a
   * task. Controls read these two off the controller rather than threading
   * them through `ToolbarControlContext.options`, since every paid action on
   * this operator needs both and none of them vary per control.
   */
  taskid: string | null = null;
  sensorId: string | null = null;
  attach(taskid: string, sensorId: string): void {
    this.taskid = taskid;
    this.sensorId = sensorId;
  }

  // ── Callbacks the map facade wires (marker placement, centering) ───────────
  onCenterVessel: ((mmsi: string) => void) | null = null;
  onActivateAll: ((keys?: string[]) => void) | null = null;
  onTakeOffMap: ((mmsi: string) => void) | null = null;
  onClearAll: (() => void) | null = null;
  /** Is this hull currently drawn? — the Watched Vessels drawer's "ON MAP" badge, mirrors the sidebar's own `onMapKeys`. */
  onIsOnMap: ((mmsi: string) => boolean) | null = null;
  /**
   * Registered by the Port Calls control so `reset()` reaches it on a
   * task/project switch. That drawer holds a paid result set and its own form
   * state, neither of which lives in this controller — and neither of which
   * belongs to the task the user just switched TO. The previous arrangement
   * (a component-scoped service the host reset by hand) went away with the
   * drawer's active-vessel subject; this is the same guarantee in one line.
   */
  onResetPortCalls: (() => void) | null = null;
  /**
   * Registered by the Port Calls drawer so clicking a marker (or picking a
   * list row) offers that hull to its identifier box.
   *
   * NOT the old subject: this fills in a field the user can then edit, add to
   * or replace, and it decides nothing about what gets charged. The drawer
   * still spends on whatever the box says at the moment the button is pressed.
   */
  onActiveVesselChange: ((row: VesselFinderVesselRow | null) => void) | null = null;

  /** The layer reports its active hull here; the facade owns nothing else about it. */
  setActiveVessel(row: VesselFinderVesselRow | null): void {
    this.onActiveVesselChange?.(row);
  }

  // ── Search ──────────────────────────────────────────────────────────────────
  setSearchResults(searchResults: VesselFinderVesselRow[]): void {
    this.patch({ searchResults: searchResults.map(sanitizeRow) });
  }
  setUseSatellite(useSatellite: boolean): void {
    this.patch({ useSatellite });
  }

  // ── Located vessels ───────────────────────────────────────────────────────
  /** Kept here (the newest `MAX_LOCATED`), same cap Voyage Radar's own Watched Vessels list uses. */
  setLocatedVessels(locatedVessels: VesselFinderVesselRow[]): void {
    this.patch({ locatedVessels: locatedVessels.map(sanitizeRow).slice(-MAX_LOCATED) });
  }

  /** Insert a new hull, or merge onto one already located (by mmsi, or imo when no mmsi is known). */
  upsertLocatedVessel(row: VesselFinderVesselRow): void {
    const list = this.snapshot().locatedVessels ?? [];
    const key = (r: VesselFinderVesselRow) => r.mmsi ?? r.imo;
    const idx = list.findIndex((r) => key(r) != null && key(r) === key(row));
    const next =
      idx === -1
        ? [...list, row]
        : list.map((r, i) => (i === idx ? { ...r, ...row } : r));
    this.setLocatedVessels(next);
  }

  /** Take a hull off the located list (un-keep) — free, the inverse of a save. */
  removeLocatedVessel(identifier: string): void {
    const list = this.snapshot().locatedVessels ?? [];
    this.setLocatedVessels(list.filter((r) => r.mmsi !== identifier && r.imo !== identifier));
  }

  // ── Actions the drawer rows call ────────────────────────────────────────────
  centerVessel(mmsi: string): void {
    this.onCenterVessel?.(mmsi);
  }
  activateAll(keys?: string[]): void {
    this.onActivateAll?.(keys);
  }
  takeOffMap(mmsi: string): void {
    this.removeLocatedVessel(mmsi);
    this.onTakeOffMap?.(mmsi);
  }
  clearAll(): void {
    this.setLocatedVessels([]);
    this.onClearAll?.();
  }
  /** Read-only — no dispatch, so the toolbar drawer can call it straight from its template. */
  isOnMap(mmsi: string): boolean {
    return this.onIsOnMap?.(mmsi) ?? false;
  }

  snapshot(): VesselFinderState {
    return this._state$.value;
  }

  private patch(patch: Partial<VesselFinderState>): void {
    this._state$.next({ ...this._state$.value, ...patch });
  }

  // ── PlaybackController contract — a located-vessel list has no transport ────
  togglePlay(): void {}
  play(): void {}
  pause(): void {}
  seek(_index: number): void {}
  stepForward(): void {}
  stepBack(): void {}
  skipToStart(): void {}
  skipToEnd(): void {}
  setSpeed(_n: number): void {}
  toggleLabels(): void {}
  setTypeFilter(_set: Set<string>): void {}
  enterPlayback(_fromIso: string, _toIso: string): Promise<number> {
    return Promise.resolve(0);
  }
  resumeLive(): void {}
  close(): void {
    this.setLocatedVessels([]);
    this.setSearchResults([]);
  }

  reset(): void {
    this.taskid = null;
    this.sensorId = null;
    this.onResetPortCalls?.();
    this.setLocatedVessels([]);
    this.setSearchResults([]);
    this.setUseSatellite(false);
  }
}
